import React from 'react';
import { Box, Chip, FormControlLabel, Switch, Typography } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_OUTLINE_VARIANT, BRAND_SURFACE_CONTAINER } from '../brandDashboardTokens';
import type { CampaignReportPreviewScenario } from './campaignReportMockData';
import { isCampaignReportDevToolsEnabled } from './campaignReportMockData';

export type CampaignReportDataMode = 'live' | 'preview';

const PREVIEW_SCENARIOS: { value: CampaignReportPreviewScenario; label: string }[] = [
    { value: 'full', label: 'Full dashboard' },
    { value: 'syncing', label: 'Syncing' },
    { value: 'empty', label: 'Empty' },
];

export interface CampaignReportDevToolbarProps {
    dataMode: CampaignReportDataMode;
    onDataModeChange: (mode: CampaignReportDataMode) => void;
    previewScenario: CampaignReportPreviewScenario;
    onPreviewScenarioChange: (scenario: CampaignReportPreviewScenario) => void;
}

export function CampaignReportDevToolbar({
    dataMode,
    onDataModeChange,
    previewScenario,
    onPreviewScenarioChange,
}: CampaignReportDevToolbarProps) {
    if (!isCampaignReportDevToolsEnabled()) return null;

    const isPreview = dataMode === 'preview';

    return (
        <Box
            sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                bgcolor: BRAND_SURFACE_CONTAINER,
                border: `1px dashed ${BRAND_LUXE_PRIMARY}66`,
            }}>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 11,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: BRAND_LUXE_PRIMARY,
                    mb: 1.5,
                }}>
                Dev preview
            </Typography>
            <FormControlLabel
                control={
                    <Switch
                        checked={isPreview}
                        onChange={e => onDataModeChange(e.target.checked ? 'preview' : 'live')}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                bgcolor: `${BRAND_LUXE_PRIMARY}66`,
                            },
                        }}
                    />
                }
                label={
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#fff' }}>
                        {isPreview ? 'Design preview (mock data)' : 'Live API data'}
                    </Typography>
                }
                sx={{ m: 0, mb: isPreview ? 1.5 : 0 }}
            />
            {isPreview ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {PREVIEW_SCENARIOS.map(opt => (
                        <Chip
                            key={opt.value}
                            label={opt.label}
                            size="small"
                            onClick={() => onPreviewScenarioChange(opt.value)}
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                bgcolor: previewScenario === opt.value ? BRAND_LUXE_PRIMARY : 'transparent',
                                color: previewScenario === opt.value ? '#392700' : 'rgba(255,255,255,0.65)',
                                border: `1px solid ${
                                    previewScenario === opt.value ? BRAND_LUXE_PRIMARY : BRAND_OUTLINE_VARIANT
                                }`,
                            }}
                        />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}
